import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Mail,
  Smartphone,
  MessageSquare,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  Eye,
  Send,
  FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppointmentsManagement = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Erreur lors du chargement des rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchAppointments();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleConfirmAppointment = async (appointment) => {
    try {
      await axios.put(`${API}/appointments/${appointment.id}`, { status: 'confirmed' });
      toast.success('Rendez-vous confirmé ! Le client recevra une confirmation par email.');
      fetchAppointments();
      setShowModal(false);
    } catch (error) {
      console.error('Error confirming appointment:', error);
      toast.error('Erreur lors de la confirmation');
    }
  };

  const handleConvertToRepair = (appointment) => {
    const repairData = {
      client_name: appointment.client_name,
      client_phone: appointment.client_phone,
      client_email: appointment.client_email || '',
      device: appointment.device_info || '',
      problem_description: `RDV du ${formatDate(appointment.appointment_date)}\nService: ${appointment.service_type}\n${appointment.notes || ''}`,
      status: 'en_cours'
    };
    
    navigate('/repairs/new', { state: { prefillData: repairData, fromAppointment: appointment.id } });
  };

  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowModal(true);
  };

  const handleDelete = async (appointmentId, clientName) => {
    if (!window.confirm(`Supprimer le rendez-vous de ${clientName} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      toast.success('Rendez-vous supprimé');
      fetchAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
      confirmed: { label: 'Confirmé', className: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      cancelled: { label: 'Annulé', className: 'bg-red-100 text-red-800', icon: XCircle },
      completed: { label: 'Terminé', className: 'bg-green-100 text-green-800', icon: CheckCircle }
    };
    
    const info = statusMap[status] || statusMap.pending;
    const Icon = info.icon;
    
    return (
      <Badge className={info.className}>
        <Icon className="w-3 h-3 mr-1" />
        {info.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredAppointments = filterStatus === 'all' 
    ? appointments 
    : appointments.filter(a => a.status === filterStatus);

  const upcomingAppointments = filteredAppointments.filter(a => 
    new Date(a.appointment_date) >= new Date() && a.status !== 'cancelled'
  );

  const pastAppointments = filteredAppointments.filter(a => 
    new Date(a.appointment_date) < new Date() || a.status === 'cancelled'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Calendar className="w-8 h-8" />
          Gestion des Rendez-vous
        </h1>
        <p className="text-gray-600 mt-2">
          {appointments.length} rendez-vous au total
        </p>
      </div>

      {/* Store Hours Card */}
      <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-3">Horaires du Magasin</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Lundi</p>
                  <p className="font-semibold text-sm">13h-19h</p>
                </div>
                <div className="bg-white p-3 rounded-lg md:col-span-2">
                  <p className="text-xs text-gray-600">Mardi - Samedi</p>
                  <p className="font-semibold text-sm">10h-13h | 14h-19h</p>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <p className="text-xs text-gray-600">Dimanche</p>
                  <p className="font-semibold text-sm text-gray-500">Fermé</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">Filtrer par statut :</span>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="confirmed">Confirmé</SelectItem>
                <SelectItem value="completed">Terminé</SelectItem>
                <SelectItem value="cancelled">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Modal for Appointment Details */}
      {showModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="border-b bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-6 h-6" />
                  Détails du Rendez-vous
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowModal(false)}
                  className="text-white hover:bg-white/20"
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedAppointment.status)}
                <span className="text-sm text-gray-500">
                  Créé le {formatDate(selectedAppointment.created_at)}
                </span>
              </div>

              {/* Appointment Time */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date et heure</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatDate(selectedAppointment.appointment_date)}
                    </p>
                    <p className="text-lg font-semibold text-blue-600">
                      à {formatTime(selectedAppointment.appointment_date)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700">Client</span>
                  </div>
                  <p className="text-gray-900 font-medium">{selectedAppointment.client_name}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700">Téléphone</span>
                  </div>
                  <a href={`tel:${selectedAppointment.client_phone}`} className="text-blue-600 hover:underline">
                    {selectedAppointment.client_phone}
                  </a>
                </div>

                {selectedAppointment.client_email && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-700">Email</span>
                    </div>
                    <a href={`mailto:${selectedAppointment.client_email}`} className="text-blue-600 hover:underline break-all">
                      {selectedAppointment.client_email}
                    </a>
                  </div>
                )}

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700">Service demandé</span>
                  </div>
                  <p className="text-gray-900">{selectedAppointment.service_type}</p>
                </div>
              </div>

              {selectedAppointment.device_info && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-semibold text-orange-700">Appareil concerné</span>
                  </div>
                  <p className="text-gray-900">{selectedAppointment.device_info}</p>
                </div>
              )}

              {selectedAppointment.notes && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-700">Notes / Détails</span>
                  </div>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedAppointment.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="border-t pt-6 space-y-3">
                <p className="text-sm font-semibold text-gray-700 mb-3">Actions disponibles :</p>
                
                {selectedAppointment.status === 'pending' && (
                  <Button 
                    onClick={() => handleConfirmAppointment(selectedAppointment)}
                    className="w-full bg-green-600 hover:bg-green-700"
                    size="lg"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Confirmer et Envoyer Email au Client
                  </Button>
                )}

                {(selectedAppointment.status === 'confirmed' || selectedAppointment.status === 'pending') && (
                  <Button 
                    onClick={() => handleConvertToRepair(selectedAppointment)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Transformer en Réparation
                  </Button>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={() => window.open(`https://wa.me/${selectedAppointment.client_phone.replace(/\s/g, '')}`, '_blank')}
                    variant="outline"
                    className="flex-1 text-green-600 hover:bg-green-50"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>

                  <Button 
                    onClick={() => window.location.href = `tel:${selectedAppointment.client_phone}`}
                    variant="outline"
                    className="flex-1"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler
                  </Button>
                </div>

                {selectedAppointment.status !== 'cancelled' && (
                  <Button 
                    onClick={() => {
                      handleStatusChange(selectedAppointment.id, 'cancelled');
                      setShowModal(false);
                    }}
                    variant="outline"
                    className="w-full text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Annuler le Rendez-vous
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Upcoming Appointments */}
      {upcomingAppointments.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            📅 Rendez-vous à venir ({upcomingAppointments.length})
          </h2>
          <div className="space-y-4">
            {upcomingAppointments.map((appointment) => (
              <Card key={appointment.id} className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {appointment.client_name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {appointment.service_type}
                          </p>
                        </div>
                        {getStatusBadge(appointment.status)}
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{formatDate(appointment.appointment_date)}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-gray-700">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold">{formatTime(appointment.appointment_date)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleViewDetails(appointment)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir Détails
                      </Button>
                      
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(appointment.id, appointment.client_name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Past Appointments */}
      {pastAppointments.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-500 mb-4">
            📋 Historique ({pastAppointments.length})
          </h2>
          <div className="space-y-3">
            {pastAppointments.map((appointment) => (
              <Card key={appointment.id} className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-sm">
                        <p className="font-semibold text-gray-900">{appointment.client_name}</p>
                        <p className="text-gray-600">{formatDate(appointment.appointment_date)} à {formatTime(appointment.appointment_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(appointment.status)}
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleViewDetails(appointment)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleDelete(appointment.id, appointment.client_name)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {filteredAppointments.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucun rendez-vous
            </h3>
            <p className="text-gray-600">
              Les rendez-vous pris en ligne apparaîtront ici
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AppointmentsManagement;
